import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const Navbar = ({ sidebarOpen, setSidebarOpen }) => {
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warmupLoading, setWarmupLoading] = useState(false);

  useEffect(() => {
    fetchSystemStatus();
    // TEMPORARILY DISABLED: Auto-refresh to fix API overload
    // const interval = setInterval(fetchSystemStatus, 30000);
    // return () => clearInterval(interval);
  }, []);

  const fetchSystemStatus = async () => {
    try {
      // Use MongoDB system health endpoint directly
      const response = await fetch('/api/v1/system/health');
      const data = await response.json();
      
      console.log('MongoDB system status response:', data); // Debug log
      
      if (data && data.success && data.status === 'healthy') {
        setSystemStatus({ 
          system_healthy: true, 
          services: data.services || {},
          database: data.database,
          mongodb_only: true
        });
      } else {
        setSystemStatus({ 
          system_healthy: false, 
          error: data.error || 'MongoDB system unhealthy' 
        });
      }
    } catch (error) {
      console.error('Failed to fetch MongoDB system status:', error);
      setSystemStatus({ system_healthy: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchSystemStatus();
    toast.info('🔄 Refreshing system status...');
    // Refresh the current page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleRunWarmup = async () => {
    setWarmupLoading(true);
    try {
      toast.info('🚀 Starting warmup cycle...');
      const response = await apiService.runManualWarmup({ test_mode: false });
      toast.success(`✅ Warmup completed! Sent ${response.data.total_emails_sent} emails`);
      // Refresh status after warmup
      setTimeout(() => {
        fetchSystemStatus();
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Warmup error:', error);
      
      // Handle different error types
      if (error.response?.status === 429) {
        toast.error(`⚠️ ${error.response.data.detail}`);
      } else {
        toast.error('❌ Failed to run warmup cycle');
      }
    } finally {
      setWarmupLoading(false);
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 fixed w-full z-30 top-0">
      <div className="px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {/* Mobile hamburger menu */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden mr-3"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            
            <div className="flex items-center">
              <div className="gradient-bg text-white rounded-lg p-2 mr-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900">Email Warmup System</h1>
                <p className="text-sm text-gray-500">Maximize Inbox Deliverability</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900">Email Warmup</h1>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* System Status */}
            <div className="flex items-center space-x-2">
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="loading-spinner"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    systemStatus?.system_healthy ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <span className={`text-sm font-medium ${
                    systemStatus?.system_healthy ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {systemStatus?.system_healthy ? 'System Healthy' : 'System Issues'}
                  </span>
                </div>
              )}
            </div>

            {/* Stats Summary */}
            {systemStatus && systemStatus.system_healthy && (
              <div className="hidden md:flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{systemStatus.total_accounts} accounts</span>
                </div>
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <span>{systemStatus.unique_domains} domains</span>
                </div>
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{systemStatus.cycles_today}/{systemStatus.max_cycles} cycles today</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center px-2 py-1.5 sm:px-3 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200 disabled:opacity-50"
                title="Refresh Dashboard"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''} sm:mr-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0V9a8 8 0 1115.356 2M15 15v4a1 1 0 01-1 1H4" />
                </svg>
                <span className="hidden sm:inline">
                  {loading ? 'Refreshing...' : 'Refresh'}
                </span>
              </button>
              
              <button
                onClick={handleRunWarmup}
                disabled={warmupLoading}
                className="flex items-center px-2 py-1.5 sm:px-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-md transition-all duration-200 disabled:opacity-50 shadow-sm"
                title="Run Manual Warmup"
              >
                <svg className={`w-4 h-4 ${warmupLoading ? 'animate-spin' : ''} sm:mr-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="hidden sm:inline">
                  {warmupLoading ? 'Running...' : 'Run Warmup'}
                </span>
              </button>
            </div>

            {/* Time Display */}
            <div className="hidden md:block text-sm text-gray-500">
              {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
